import React, { useState, useEffect } from 'react';
import { Plus, AlertTriangle } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { SessionManager } from '../../../utils/sessionManager';
import { apiGet } from '../../../utils/api';
import { useProjects } from '../../../context/ProjectContext';
import LeaderboardWidget from '../../leaderboard/LeaderboardWidget';
import OverviewBrief from './overview/OverviewBrief';
import OverviewNews from './overview/OverviewNews';
import AtAGlance from './overview/AtAGlance';
import RecentActivity from './overview/RecentActivity';

function Overview() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { selectProject } = useProjects();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ chatCount: 0, lastActive: null });
  const [recentChats, setRecentChats] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const chatsResponse = await apiGet('/chats');
        if (!chatsResponse.ok) throw new Error('Failed to fetch chats');

        const data = await chatsResponse.json();
        const chats = data.chats || [];

        let lastActive = null;
        if (chats.length > 0) {
          lastActive = new Date(Math.max(...chats.map((c) => new Date(c.updated_at))));
        }

        setStats({ chatCount: chats.length, lastActive });
        setRecentChats(
          [...chats].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 8)
        );
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load your dashboard. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const startNewChat = () => {
    selectProject(null);
    localStorage.removeItem('unified_current_chat_id');
    localStorage.removeItem('unified_current_session');
    localStorage.removeItem('unified_rag_session_id');
    localStorage.removeItem('jpro_current_chat_id');
    localStorage.removeItem('jpro_current_session');
    SessionManager.clearSessionRedundantly();
    navigate('/dashboard/unified-chat');
  };

  const firstName = currentUser?.firstName || currentUser?.first_name || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
  const hasNews = currentUser?.allowedFeatures?.includes?.('news_feed') ?? true;

  return (
    <div className="h-full bg-gray-50 dark:bg-gray-900 overflow-y-auto lg:overflow-hidden">
      <div className="max-w-screen-2xl mx-auto w-full p-5 lg:p-6 flex flex-col gap-4 lg:h-full">
        {/* Header */}
        <header className="shrink-0 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 dark:text-gray-500">
              {today}
            </p>
            <h1 className="mt-1 font-dm-serif text-[28px] lg:text-[32px] leading-tight text-gray-900 dark:text-white">
              {greeting}, {firstName}.
            </h1>
          </div>
          <button
            onClick={startNewChat}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors shadow-sm self-start sm:self-auto"
          >
            <Plus size={16} /> New Chat
          </button>
        </header>

        {error && (
          <div className="shrink-0 flex items-center gap-2 rounded-xl border border-orange-200 dark:border-orange-900/40 bg-orange-50 dark:bg-orange-900/10 px-4 py-3 text-orange-700 dark:text-orange-300">
            <AlertTriangle size={16} />
            <p className="text-sm font-medium">{error}</p>
          </div>
        )}

        {/* Main overview (left ~62%) + news rail (right ~38%). Fills the viewport;
            the long lists flex to fill and scroll internally, so the page itself
            doesn't scroll and there's no dead space when the leaderboard is absent. */}
        <div className={`grid grid-cols-1 lg:[grid-template-rows:auto_auto_1fr] gap-4 lg:flex-1 lg:min-h-0 ${hasNews ? 'lg:grid-cols-[1.6fr_1fr]' : 'lg:grid-cols-1'}`}>
          {/* 1. At a Glance — top-left on desktop */}
          <div className="order-1 lg:order-none lg:col-start-1 lg:row-start-1">
            <AtAGlance chatCount={stats.chatCount} lastActive={stats.lastActive} loading={loading} />
          </div>

          {/* 2. Leaderboard — below stats on desktop */}
          <div className="order-2 lg:order-none lg:col-start-1 lg:row-start-2">
            <LeaderboardWidget />
          </div>

          {/* 3. News rail — Brief + Latest News. Only when the user has the
              news feature (matches the gate on the route/sidebar; avoids
              firing /news/* for users without access). */}
          {hasNews && (
            <div className="order-3 lg:order-none lg:col-start-2 lg:row-start-1 lg:row-span-3 flex flex-col gap-4 lg:min-h-0">
              <OverviewBrief />
              <div className="lg:flex-1 lg:min-h-0">
                <OverviewNews />
              </div>
            </div>
          )}

          {/* 4. Recent Activity — fills remaining left column on desktop */}
          <div className="order-4 lg:order-none lg:col-start-1 lg:row-start-3 lg:min-h-0">
            <RecentActivity chats={recentChats} loading={loading} onStartChat={startNewChat} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default Overview;
